import './App.css';
import './i18n';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SidebarLayout } from '@/layouts/SidebarLayout';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { applyTheme, getTheme } from '@/components/ThemeCustomizer';
import { Login } from '@/pages/Login';
import { AuthCallback } from '@/components/AuthCallback';
import { Clients } from '@/pages/Clients';
import { Employees } from '@/pages/Employees';
import { EmployeeDetails } from '@/pages/EmployeeDetails';
import { Users } from '@/pages/Users';
import { Tracking } from '@/pages/Tracking';
import Statement from '@/pages/Statement';
import ClientHome from '@/pages/ClientHome';
import { WorkPlans } from '@/pages/WorkPlans';
import AppSettings from '@/pages/AppSettings';
import Requests from '@/pages/Requests';
import Chat from '@/pages/Chat';
import CallsLog from '@/pages/CallsLog';
import External from '@/pages/External';
import Receipt from '@/pages/Receipt';
import { MSupport as MSupportChat } from '@/mobile/MChat';
import MobileLayout from '@/mobile/MobileLayout';
import { MServices, MServiceDetail } from '@/mobile/MServices';
import MInquiries from '@/mobile/MInquiries';
import { MRequests, MRequestDetail } from '@/mobile/MRequests';
import MAccount from '@/mobile/MAccount';
import { MHome, MDashboard, MMenu } from '@/mobile/MHome';
import { MClientHome, MClientTransactions, MClientWorkers, MClientWorkerDetail, MClientRequestPlan, MClientDeposit } from '@/mobile/MClient';

// Apply theme on load
applyTheme(getTheme());

const ProtectedRoute = ({ children, adminOnly }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: 'var(--theme-accent, #D4AF37)' }} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
};

const RoleHome = () => {
  const { user } = useAuth();
  return <Navigate to={user?.role === 'admin' ? '/work-plans' : '/home'} replace />;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: 'var(--theme-accent, #D4AF37)' }} />
      </div>
    );
  }
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/receipt/:id" element={<Receipt />} />
      <Route path="/m" element={<MobileLayout />}>
        <Route index element={<MServices />} />
        <Route path="home" element={<MHome />} />
        <Route path="dashboard" element={<MDashboard />} />
        <Route path="support" element={<MSupportChat />} />
        <Route path="menu" element={<MMenu />} />
        <Route path="service/:id" element={<MServiceDetail />} />
        <Route path="inquiries" element={<MInquiries />} />
        <Route path="requests" element={<MRequests />} />
        <Route path="requests/:id" element={<MRequestDetail />} />
        <Route path="account" element={<MAccount />} />
        <Route path="client/transactions" element={<MClientTransactions />} />
        <Route path="client/workers" element={<MClientWorkers />} />
        <Route path="client/workers/:id" element={<MClientWorkerDetail />} />
        <Route path="client/request" element={<MClientRequestPlan />} />
        <Route path="client/deposit" element={<MClientDeposit />} />
      </Route>
      <Route path="/*" element={
        <ProtectedRoute>
          <SidebarLayout>
            <Routes>
              <Route path="/" element={<RoleHome />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/employees" element={<Employees />} />
              <Route path="/employees/:empId" element={<EmployeeDetails />} />
              <Route path="/tracking" element={<Tracking />} />
              <Route path="/statement" element={<Statement />} />
              <Route path="/home" element={<ClientHome />} />
              <Route path="/work-plans" element={<ProtectedRoute adminOnly><WorkPlans /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute adminOnly><Users /></ProtectedRoute>} />
              <Route path="/app-settings" element={<ProtectedRoute adminOnly><AppSettings /></ProtectedRoute>} />
              <Route path="/requests" element={<ProtectedRoute adminOnly><Requests /></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute adminOnly><Chat /></ProtectedRoute>} />
              <Route path="/calls-log" element={<ProtectedRoute adminOnly><CallsLog /></ProtectedRoute>} />
              <Route path="/external" element={<ProtectedRoute adminOnly><External /></ProtectedRoute>} />
              <Route path="*" element={<RoleHome />} />
            </Routes>
          </SidebarLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors offset="calc(env(safe-area-inset-top, 0px) + 96px)" mobileOffset="calc(env(safe-area-inset-top, 0px) + 96px)" toastOptions={{ style: { fontSize: 14, fontWeight: 700 } }} />
    </div>
  );
}

export default App;
